import { SurveyTemplateDto } from '@families-link/shared';
import { FC, useCallback, useMemo } from 'react';
import { CoreTableActionsCell, CellAction } from '../../../_core/components/_ui/core-table';
import { SurveyTemplatesTableHandlers } from './survey-templates-table.component';
import { AnalysticsIcon, AttachIcon, DeleteIcon, EditIcon, EyeIcon } from '../../../_core/components/icons';
import { useLocation } from 'react-router-dom';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import { CoreTooltip } from '../../../_core/components/_ui/core-tooltip';
import { useTranslation } from 'react-i18next';

export interface SurveyTemplatesTableActionsProps extends SurveyTemplatesTableHandlers {
	surveyTemplate: SurveyTemplateDto;
	adminTemplate: boolean;
}

export const SurveyTemplatesTableActions: FC<SurveyTemplatesTableActionsProps> = (props) => {
	const { surveyTemplate, onEdit, onDelete, onView, onAttachDistrict, adminTemplate } = props;
	const [t] = useTranslation();
	const location = useLocation();
	const isAdminRoute = location.pathname.startsWith('/admin');

	const handleDeleteSurveyTemplate = useCallback(() => {
		if (!surveyTemplate) {
			return;
		}

		onDelete?.(surveyTemplate);
	}, [onDelete, surveyTemplate]);

	const handleEditSurveyTemplate = useCallback(() => {
		if (!surveyTemplate) {
			return;
		}

		onEdit?.(surveyTemplate);
	}, [onEdit, surveyTemplate]);

	const handleViewSurveyTemplate = useCallback(() => {
		if (!surveyTemplate) {
			return;
		}

		onView?.(surveyTemplate);
	}, [onView, surveyTemplate]);

	const handleAttachDistrictToSurveyTemplate = useCallback(() => {
		if (!surveyTemplate) {
			return;
		}

		onAttachDistrict?.(surveyTemplate);
	}, [onAttachDistrict, surveyTemplate]);

	const actions = useMemo<CellAction[]>(() => {
		const lockImageObject: CellAction = {
			id: 'lock',
			type: 'react-node',
			node: (
				<CoreTooltip title={'This survey is managed by Superadmin'} placement="top" arrow>
					<HttpsOutlinedIcon color="primary" fontSize="small" />
				</CoreTooltip>
			),
		};

		return [
			...(!isAdminRoute && adminTemplate ? [lockImageObject] : []),
			{
				id: 'view',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.analystics-details'),
				props: {
					children: <AnalysticsIcon sx={{ fontSize: 16, color: (theme) => theme.colors.lightBlue }} />,
					onClick: handleViewSurveyTemplate,
				},
			},
			{
				id: 'attach',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.attach-districts'),
				icon: <AttachIcon color="primary" />,
				onClick: handleAttachDistrictToSurveyTemplate,
				props: {
					children: <AttachIcon sx={{ fontSize: 16, color: (theme) => theme.colors.lightBlue }}  />,
					onClick: handleAttachDistrictToSurveyTemplate,
					hidden: !isAdminRoute,
				},
			},
			{
				id: 'edit',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.edit'),
				props: {
					children: <EditIcon sx={{ fontSize: 24, color: (theme) => !isAdminRoute && adminTemplate? theme.colors.gray : theme.colors.lightBlue }}  />,
					onClick: handleEditSurveyTemplate,
					disabled: !isAdminRoute && adminTemplate
				},
			},
			{
				id: 'delete',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.delete'),
				props: {
					children: <DeleteIcon sx={{ fontSize: 24, color: (theme) => !isAdminRoute && adminTemplate? theme.colors.gray : theme.colors.lightBlue }}  />,
					onClick: handleDeleteSurveyTemplate,
					disabled: !isAdminRoute && adminTemplate
				},
			},
		];
	}, [
		handleEditSurveyTemplate,
		handleViewSurveyTemplate,
		handleDeleteSurveyTemplate,
		handleAttachDistrictToSurveyTemplate,
		adminTemplate,
		isAdminRoute,
	]);

	return <CoreTableActionsCell actions={actions} />;
};
